import { CustomFieldValidator } from '@backstage/plugin-scaffolder-react';

import {
  KaapClusterNodeGroupValue,
  KaapGitOpsConfigValue,
  KaapTenantFormData,
  KaapTenantFormUiOptions,
} from './types';

const TENANT_NAME_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const SLACK_CHANNEL_PATTERN = /^[a-z0-9][a-z0-9_-]*[a-z0-9]$/;
const URL_PATTERN = /^https?:\/\/[^ ]+$/;

export const validateTenantName = (name: string): string => {
  if (!name) return 'Name is required';
  if (!TENANT_NAME_PATTERN.test(name)) {
    return 'Name must consist of lower case alphanumeric characters or -';
  }
  return '';
};

export const validateSlackChannel = (slackChannel: string): string => {
  if (!slackChannel || !slackChannel.trim()) return 'Slack channel is required';
  if (!SLACK_CHANNEL_PATTERN.test(slackChannel)) {
    return 'Slack channel name has an invalid format';
  }
  return '';
};

export const validateOwner = (owner: string): string =>
  owner && owner.trim() ? '' : 'Business owner is required';

export const validateSecurityGroups = (groups: string[]): string =>
  groups && groups.length > 0 ? '' : 'Security groups required';

export const validateApplication = (application: string): string =>
  application && application.trim() ? '' : 'Application is required';

export const validateCluster = (
  clusterSelection: KaapClusterNodeGroupValue,
): string => {
  if (!clusterSelection?.clusterId) return 'Cluster is required';
  if (!clusterSelection.nodeGroups || clusterSelection.nodeGroups.length === 0) {
    return 'Node groups required';
  }
  return '';
};

export const validateQuotas = (data: KaapTenantFormData): string => {
  const entries: Array<[keyof KaapTenantFormData, string]> = [
    ['namespaceQuota', 'Namespace quota is required'],
    ['cpuRequest', 'CPU requests is required'],
    ['cpuLimit', 'CPU limits is required'],
    ['memoryRequest', 'Memory requests is required'],
    ['memoryLimit', 'Memory limits is required'],
  ];
  for (const [key, message] of entries) {
    const value = data[key] as string | undefined;
    if (!value || !String(value).trim()) return message;
  }
  return '';
};

export const validateGitOps = (config?: KaapGitOpsConfigValue): string => {
  if (!config?.enabled) return '';
  if (!config.gitRepos.length || config.gitRepos.some(url => !URL_PATTERN.test(url))) {
    return 'Each Git repository must be a valid URL';
  }
  if (!config.rootPath || !config.rootPath.trim()) {
    return 'Root path is required when GitOps is enabled';
  }
  if (config.helmRepos.some(url => !URL_PATTERN.test(url))) {
    return 'Each Helm repository must be a valid URL';
  }
  return '';
};

export const kaapTenantFormValidation: CustomFieldValidator<
  KaapTenantFormData,
  KaapTenantFormUiOptions
> = (value, field) => {
  if (!value) {
    field.addError('Tenant data is required');
    return;
  }

  const checks = [
    !value.stage ? 'Stage is required' : '',
    validateTenantName(value.tenantName),
    validateOwner(value.owner),
    validateSecurityGroups(value.securityObjectIds),
    validateApplication(value.application),
    validateSlackChannel(value.slackChannel),
    validateCluster(value.clusterSelection),
    validateQuotas(value),
    validateGitOps(value.gitopsConfig),
  ];

  const firstError = checks.find(Boolean);
  if (firstError) {
    field.addError(firstError);
  }
};
